import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Switch, FormControlLabel,
  CircularProgress, Alert, Divider, Typography,
  Autocomplete, Box, Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as LinkedIcon,
} from '@mui/icons-material';
import corporateService, { CorporateEmployee, PagaliUser } from '../../services/corporate.service';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  clientId: number;
  initial?: CorporateEmployee;
}

const BRAND = '#6233A0';

const EmployeeModal: React.FC<Props> = ({ open, onClose, onSaved, clientId, initial }) => {
  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    departamento: '',
    cargo: '',
    reward_amount: '',
    ativo: true,
  });

  // Pesquisa de utilizador Pagali
  const [userInput, setUserInput] = useState('');
  const [userOptions, setUserOptions] = useState<PagaliUser[]>([]);
  const [userSelected, setUserSelected] = useState<PagaliUser | null>(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const [nfcUid, setNfcUid] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigningNfc, setAssigningNfc] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        nome: initial.nome || '',
        email: initial.email || '',
        telefone: initial.telefone || '',
        departamento: initial.departamento || '',
        cargo: initial.cargo || '',
        reward_amount: String(initial.reward_amount || ''),
        ativo: !!initial.ativo,
      });
      setNfcUid(initial.nfc_uid || '');
      setUserSelected(initial.client_id ? { user_id: initial.client_id, nome: initial.nome } : null);
      setUserInput('');
    } else {
      setForm({ nome: '', email: '', telefone: '', departamento: '', cargo: '', reward_amount: '', ativo: true });
      setNfcUid('');
      setUserSelected(null);
      setUserInput('');
    }
    setError(null);
  }, [initial, open]);

  // Pesquisa de utilizadores com debounce
  const searchUsers = useCallback(async (q: string) => {
    if (q.length < 2) { setUserOptions([]); return; }
    setUserSearchLoading(true);
    try {
      const results = await corporateService.searchUsers(q);
      setUserOptions(results);
    } catch {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(userInput), 350);
    return () => clearTimeout(timer);
  }, [userInput, searchUsers]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));

  const handleUserSelect = (_: any, value: PagaliUser | null) => {
    setUserSelected(value);
    if (value) {
      setForm(f => ({
        ...f,
        nome: f.nome || value.nome,
        email: f.email || value.email || '',
        telefone: f.telefone || value.telefone || '',
      }));
    }
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { setError('Nome é obrigatório'); return; }
    if (!form.reward_amount || isNaN(parseFloat(form.reward_amount))) {
      setError('Valor de recompensa inválido'); return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...form,
        reward_amount: parseFloat(form.reward_amount),
        ativo: form.ativo ? 1 : 0,
        client_id: userSelected?.user_id || undefined,
      };
      if (initial?.id) {
        await corporateService.updateEmployee(clientId, initial.id, payload);
      } else {
        await corporateService.createEmployee(clientId, payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao guardar funcionário');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignNfc = async () => {
    if (!initial?.id || !nfcUid.trim()) { setError('Introduz o UID do bracelet'); return; }
    setAssigningNfc(true);
    setError(null);
    try {
      await corporateService.assignBracelet(clientId, initial.id, nfcUid.trim());
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao vincular bracelet');
    } finally {
      setAssigningNfc(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial?.id ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>

          {/* ── Pesquisa de conta Pagali ── */}
          <Grid item xs={12}>
            <Autocomplete
              options={userOptions}
              getOptionLabel={o => o.nome}
              loading={userSearchLoading}
              inputValue={userInput}
              onInputChange={(_, v) => setUserInput(v)}
              onChange={handleUserSelect}
              filterOptions={x => x}
              isOptionEqualToValue={(o, v) => o.user_id === v.user_id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.user_id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email || option.username}
                        {option.telefone && ` · ${option.telefone}`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Conta Pagali (pesquisar por nome ou email)"
                  placeholder="Ex: Milton Cabral ou milton@email.com"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userSearchLoading && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {userSelected && (
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <LinkedIcon fontSize="small" color="success" />
                <Typography variant="caption" color="success.main">
                  Ligado a <b>{userSelected.nome}</b>
                  {userSelected.email && ` (${userSelected.email})`}
                </Typography>
              </Box>
            )}
            {!userSelected && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                Opcional — o funcionário pode activar o bracelet na app sem conta pré-existente
              </Typography>
            )}
          </Grid>

          {/* ── Dados do funcionário ── */}
          <Grid item xs={12}>
            <TextField label="Nome *" value={form.nome} onChange={set('nome')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Email" value={form.email} onChange={set('email')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Telefone" value={form.telefone} onChange={set('telefone')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Departamento" value={form.departamento} onChange={set('departamento')} fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Cargo" value={form.cargo} onChange={set('cargo')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Recompensa Mensal (ECV) *"
              value={form.reward_amount}
              onChange={set('reward_amount')}
              type="number"
              inputProps={{ min: 0, max: 10000 }}
              helperText="Entre 2.000 e 10.000 ECV"
              fullWidth
            />
          </Grid>
          {initial?.id && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.ativo} onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))} />}
                label="Funcionário activo"
              />
            </Grid>
          )}
        </Grid>

        {/* ── Bracelet NFC ── */}
        {initial?.id && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Bracelet NFC</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <TextField
                  label="NFC UID (ex: A1:B2:C3:D4)"
                  value={nfcUid}
                  onChange={e => setNfcUid(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={initial.nfc_uid || 'Sem bracelet vinculado'}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleAssignNfc}
                  disabled={assigningNfc}
                  startIcon={assigningNfc ? <CircularProgress size={14} /> : null}
                >
                  Vincular
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#4e2880' } }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeModal;
